"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Logger = void 0;
const circularJSON = require('circular-json');
const serializeError = require('serialize-error');
const yaml = require('js-yaml');
if (!process.env.NODE_ENV && !process.env.DEPLOYMENT_ENV) {
    throw new Error('Expected either NODE_ENV or DEPLOYMENT_ENV to be set.');
}
/* eslint-disable @typescript-eslint/naming-convention */
var LogLevel;
(function (LogLevel) {
    LogLevel["trace"] = "trace";
    LogLevel["debug"] = "debug";
    LogLevel["info"] = "info";
    LogLevel["warn"] = "warn";
    LogLevel["error"] = "error";
})(LogLevel || (LogLevel = {}));
const LogLevelPriority = {
    trace: 0,
    debug: 1,
    info: 2,
    warn: 3,
    error: 4,
};
const LogLevelColor = {
    trace: '\x1b[34m',
    debug: '\x1b[36m',
    info: '\x1b[32m',
    warn: '\x1b[33m',
    error: '\x1b[31m',
};
// Data can be a regular object or an Error object.
// Log json on prod. Log regular objects on dev since it prints cleaner in the console.
// However, we should still run JSON.stringify() and serializeError on dev so we can catch
// potential cases where we might be logging something that causes an error.
class Logger {
    constructor(defaultData, defaultMeta, defaultLabels) {
        /**
         * Minimum log level to output
         * Default is LogLevel.debug, which omits 'trace' logs.
         */
        this.logLevel = LogLevel.debug;
        this.createLogger = (defaultData, defaultMeta, defaultLabels) => {
            // Merge the existing default fields with new default fields. Allows caller to
            // add more default fields without having to pass in the original defaults again.
            return new Logger({ ...this.defaultData, ...defaultData }, { ...this.defaultMeta, ...defaultMeta }, { ...this.defaultLabels, ...defaultLabels });
        };
        this.updateMeta = (defaultData, defaultMeta) => {
            this.defaultData = defaultData;
            this.defaultMeta = defaultMeta;
        };
        this.trace = (message, data, metadata) => {
            this.logTrace(LogLevel.trace, message, data, metadata);
        };
        this.debug = (message, data, metadata) => {
            this.logTrace(LogLevel.debug, message, data, metadata);
        };
        this.info = (message, data, metadata) => this.logTrace(LogLevel.info, message, data, metadata);
        this.log = this.info;
        this.warn = (message, data, metadata) => this.logTrace(LogLevel.warn, message, data, metadata);
        this.error = (message, data, metadata) => this.logTrace(LogLevel.error, message, data, metadata);
        this.logTrace = (level, message, traceData, metadata) => {
            // TODO: We should find a way of removing logs when running in the context of integration tests
            // without modifying the production code.
            if (process.env.MOCK_LOGGER)
                return;
            // Ignore logs that are lower than the minimum level
            if (LogLevelPriority[level] < LogLevelPriority[this.logLevel]) {
                return;
            }
            const occurrenceData = Logger.buildOccurrenceData(traceData);
            const data = {
                ...this.defaultData,
                ...occurrenceData,
            };
            const traceMetadata = {
                ...(this.isProd && {
                    severity: level,
                    'logging.googleapis.com/labels': {
                        application_logs: 'true',
                        service_version: process.env.SERVICE_VERSION,
                        ...(this.defaultLabels || {}),
                    },
                }),
                ...(Object.keys(data).length && { data }),
                ...this.defaultMeta,
                ...metadata,
            };
            // We should always generate serializedTrace even in dev mode.
            // See https://github.com/streamyard/streamyard/pull/15874#pullrequestreview-1530501154
            const trace = {
                ...traceMetadata,
                ...(typeof message === 'string' && { message }),
            };
            if (this.isProd) {
                // Raw JSON.stringify throws an unhandled promise rejection for circular references.
                // 'circular-json' package was about 10X faster than the 'json-stringify-safe' package on
                // large objects like the errors returned from googleapis. Seems like the 'circular-json'
                // package doesn't go as deep into the tree or something.
                // 'circular-json' takes 1ms to stringify large googleapis error
                // 'json-stringify-safe' takes 6ms to parse the same error
                const serializedTrace = circularJSON.stringify(trace);
                // eslint-disable-next-line no-console
                console[level](serializedTrace);
            }
            else {
                const levelTag = `${LogLevelColor[level]}[${level.toUpperCase()}]\x1b[0m`;
                let line = `${levelTag} ${message}`;
                if (Object.keys(traceMetadata).length) {
                    const serializedMeta = yaml
                        .dump(traceMetadata, { lineWidth: -1, skipInvalid: true })
                        // Indent everything by 4 spaces
                        .replaceAll(/^/gm, '    ');
                    line += `\n${serializedMeta}`;
                }
                // eslint-disable-next-line no-console
                console[level](line);
            }
        };
        this.defaultData = defaultData;
        this.defaultMeta = defaultMeta;
        this.defaultLabels = defaultLabels;
        this.isProd = process.env.NODE_ENV
            ? process.env.NODE_ENV === 'production'
            : process.env.DEPLOYMENT_ENV !== 'development';
        if (process.env.LOG_LEVEL) {
            this.logLevel = process.env.LOG_LEVEL;
        }
    }
}
exports.Logger = Logger;
Logger.buildOccurrenceData = (traceData) => {
    // Serialize error objects or else only enumerable properties will be kept when stringifying
    // and we will lose data.
    // https://stackoverflow.com/questions/18391212/is-it-not-possible-to-stringify-an-error-using-json-stringify
    let occurrenceData = {};
    if (traceData instanceof Error) {
        occurrenceData = { error: serializeError(traceData) };
    }
    else if (traceData instanceof Object) {
        occurrenceData = { ...traceData };
        // We also want to serialize errors that are nested in the data object
        // We assume we either have `error` or `err` property
        if (traceData.error instanceof Error) {
            occurrenceData.error = serializeError(traceData.error);
        }
        else if (traceData.err instanceof Error) {
            occurrenceData.error = serializeError(traceData.err);
            delete occurrenceData.err;
        }
    }
    return occurrenceData;
};
//# sourceMappingURL=logger.js.map